import React, { Suspense, lazy } from 'react';
import { IntlProvider } from 'react-intl';
import { Router, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { history } from 'Redux/store';
import isBot from 'Helpers/SEO/is-bot';
// import { fetchSiteBrand } from 'Redux/ducks/site-brand';
import AnonymousId from 'Features/Auth/components/AnonymousId';
import ScrollToTop from 'Components/Scroll/ScrollToTop';
import NavBar from 'Layout/NavBar/NavBar';
import ErrorBoundary from 'Components/Behaviour/ErrorHandling/ErrorBoundary';
import ModalGroupContainer from 'Components/Modal/ModalGroupContainer';
import FeedbackPopupContainer from 'Components/Feedback/FeedbackPopups/FeedbackPopupContainer';
import FooterGroupContainer from 'Layout/Footer/FooterGroupContainer';
import Route from 'App/Route';
import SkeletonPlaceholder from 'Components/Progress/SkeletonPlaceholder';
import ViewportProvider from 'Context/ViewportProvider';
import VisibleVoucherContainer from 'Components/InfoBar/VisibleVoucher/VisibleVoucherContainer';
import CookieConsentWrapper from 'Components/Legal/CookieConsent/CookieConsentWrapper';
import { MetaTag } from 'Components/SEO/Tags';

const locale = window.locale || 'en';

const { translations } = window;

const AccountRoute = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'App/AccountRoute'));
const AccountOverview = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/AccountOverview'));
const LoginAndSecurity = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/LoginAndSecurity'));
const LinkedAccounts = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/LinkedAccounts'));
const ContactPreferences = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/ContactPreferences'));
const WebsiteSettings = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/WebsiteSettings'));
const PreferenceCenter = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/PreferenceCenter'));
const CouponsAndRewards = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/CouponsAndRewards'));
const Support = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Account/pages/Support'));
const OrdersAndKeys = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Order/pages/OrdersAndKeys'));
const OrderContainer = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Order/pages/OrderContainer'));
const KeyLibrary = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Order/pages/ProductLibrary'));
const OrderItemReviewForm = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Order/pages/OrderItemReviewForm'));
const OrderItemReviewsContainer = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Order/pages/OrderItemReviewsContainer'));
const PaymentInformation = lazy(() => import(/* webpackChunkName: "AccountArea" */ 'Features/Payment/pages/AccountPaymentInformation'));

const AccountConfirm = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/AccountConfirm'));
const EmailChangeConfirm = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/EmailChangeConfirm'));
const MagicLoginContainer = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/MagicLoginContainer'));
const PasswordReset = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages//PasswordReset'));
const EpicReturnContainer = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/EpicReturnContainer'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/ResetPassword'));
const AdminLoginAs = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/AdminLoginAs'));
const Optin = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/Optin'));
const FandomOauthReturnContainer = lazy(() => import(/* webpackChunkName: "Auth" */ 'Features/Auth/pages/FandomOauthReturnContainer'));

const BestGamesOfPage = lazy(() => import(/* webpackChunkName: "BestOf" */ 'Features/BestOf/pages/BestGamesOfPage'));

const BlogPostPage = lazy(() => import(/* webpackChunkName: "BlogPage" */ 'Features/Blog/pages/BlogPostPage'));
const BlogPostHomePage = lazy(() => import(/* webpackChunkName: "BlogPage" */ 'Features/Blog/pages/BlogHomePage'));
const BlogPreviewPage = lazy(() => import(/* webpackChunkName: "BlogPage" */ 'Features/Blog/pages/BlogPreviewPage'));

const HpOmen = lazy(() => import(/* webpackChunkName: "Brands" */ 'Features/Brands/pages/HpOmen'));
const HpOmenGamingHub = lazy(() => import(/* webpackChunkName: "Brands" */ 'Features/Brands/pages/HpOmenGamingHub'));
const Pickaxe = lazy(() => import(/* webpackChunkName: "Brands" */ 'Features/StaticCategory/pages/PickaxePage'));
const WelcomePage = lazy(() => import(/* webpackChunkName: "Brands" */ 'Features/Brands/pages/WelcomePage'));
const WelcomeNewUserPage = lazy(() => import(/* webpackChunkName: "Brands" */ 'Features/Brands/pages/WelcomeNewUserPage'));

const ExtensionPage = lazy(() => import(/* webpackChunkName: "BrowserExtension" */ 'Features/BrowserExtension/pages/ExtensionPage'));
const ExtensionUninstallPage = lazy(() => import(/* webpackChunkName: "BrowserExtension" */ 'Features/BrowserExtension/pages/ExtensionUninstallPage'));

const BundlePage = lazy(() => import(/* webpackChunkName: "BundlePage" */ 'Features/Bundle/pages/BundlePage'));
const BundleCategoryPage = lazy(() => import(/* webpackChunkName: "BundleCategory" */ 'Features/BundleCategory/pages/BundleCategoryPage'));

const CheckoutPage = lazy(() => import(/* webpackChunkName: "Checkout" */ 'Features/Checkout/pages/CheckoutPage'));

const Competitions = lazy(() => import(/* webpackChunkName: "Competitions" */ 'Features/Competitions/pages/CompetitionPage'));
const CompetitionPreviewContainer = lazy(() => import(/* webpackChunkName: "Competition" */ 'Features/Competitions/pages/CompetitionPreviewContainer'));

const FacetedFacetSearch = lazy(() => import(/* webpackChunkName: "FacetCategoryPages" */ 'Features/FacetCategory/pages/FacetedFacetSearch'));
const FacetOrderedList = lazy(() => import(/* webpackChunkName: "FacetCategoryPages" */ 'Features/FacetCategory/pages/FacetOrderedList'));
const FacetSearch = lazy(() => import(/* webpackChunkName: "FacetCategoryPages" */ 'Features/FacetCategory/pages/FacetSearch'));
const GamesLike = lazy(() => import(/* webpackChunkName: "FacetCategoryPages" */ 'Features/FacetCategory/pages/GamesLike'));

const FantasyVersePage = lazy(() => import(/* webpackChunkName: "FantasyVerse" */ 'Features/FantasyVerse/pages/FantasyVersePage'));

const GiftGuide = lazy(() => import(/* webpackChunkName: "GiftGuide" */ 'Features/GiftGuide/pages/GiftGuidePage'));

const HomePage = lazy(() => import(/* webpackChunkName: "HomePage" */ 'Features/Home/pages/HomePage'));

const ImagePreviewContainer = lazy(() => import(/* webpackChunkName: "ImagePreview" */ 'Features/ImagePreview/pages/ImagePreviewContainer'));
const FeaturedCarouselFullPreviewContainer = lazy(() => import(/* webpackChunkName: "ImagePreview" */ 'Features/ImagePreview/pages/FeaturedCarouselFullPreviewContainer'));

const AboutUsPage = lazy(() => import(/* webpackChunkName: "InfoPages" */ 'Features/InfoPages/pages/AboutUsPage'));
const AffiliatesPage = lazy(() => import(/* webpackChunkName: "InfoPages" */ 'Features/InfoPages/pages/AffiliatesPage'));
const CommunityPage = lazy(() => import(/* webpackChunkName: "InfoPages" */ 'Features/InfoPages/pages/CommunityPage'));
const CookiePolicy = lazy(() => import(/* webpackChunkName: "InfoPages" */ 'Features/InfoPages/pages/CookiePolicyPage'));
const ReferralsPage = lazy(() => import(/* webpackChunkName: "InfoPages" */ 'Features/InfoPages/pages/ReferralPage'));

const SplitTestSwitch = lazy(() => import(/* webpackChunkName: "Internal" */ 'Features/Internal/pages/SplitTestSwitchPage'));

const PaymentPage = lazy(() => import(/* webpackChunkName: "PaymentPage" */ 'Features/Payment/pages/PaymentPage'));
const PaymentReturn = lazy(() => import(/* webpackChunkName: "PaymentPage" */ 'Features/Payment/pages/PaymentReturn'));
const BillingPage = lazy(() => import(/* webpackChunkName: "BillingPage" */ 'Features/Payment/pages/BillingPage'));

const PickAndMixContainer = lazy(() => import(/* webpackChunkName: "PickAndMixPage" */ 'Features/PickAndMix/pages/PickAndMixProductPage'));

const ProductPage = lazy(() => import(/* webpackChunkName: "ProductPage" */ 'Features/Product/pages/ProductPage'));
const ProductDLCPage = lazy(() => import(/* webpackChunkName: "ProductPage" */ 'Features/Product/pages/ProductDLCPage'));
const ShowcaseProduct = lazy(() => import(/* webpackChunkName: "ProductPage" */ 'Features/Product/pages/ShowcaseProduct'));

const ReceiptContainer = lazy(() => import(/* webpackChunkName: "Receipt" */ 'Features/Receipt/pages/ReceiptPage'));

const RecentlyViewedPage = lazy(() => import(/* webpackChunkName: "RecentlyViewedPage" */ 'Features/RecentlyViewed/pages/RecentlyViewedPage'));

const FanFavouritesContainer = lazy(() => import(/* webpackChunkName: "Recommended" */ 'Features/Recommended/pages/RecommendedPage'));

const RedeemCodePage = lazy(() => import(/* webpackChunkName: "Redeem" */ 'Features/Redeem/pages/RedeemCodePage'));
const RedeemGiftContainer = lazy(() => import(/* webpackChunkName: "Redeem" */ 'Features/Redeem/pages/RedeemGiftPage'));

const CyberMonday = lazy(() => import(/* webpackChunkName: "OnSalePage" */ 'Features/Sale/pages/CyberMonday'));
// const BlackFriday = lazy(() => import(/* webpackChunkName: "OnSalePage" */ 'Features/Sale/pages/BlackFridayOnSalePage'));
const OnSalePage = lazy(() => import(/* webpackChunkName: "OnSalePage" */ 'Features/Sale/pages/OnSalePage'));

const SearchPage = lazy(() => import(/* webpackChunkName: "Search" */ 'Features/Search/pages/SearchPage'));

const Showcase = lazy(() => import(/* webpackChunkName: "Showcase" */ 'Features/Showcase/pages/Showcase'));

const AllGames = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/AllGamesPage'));
const AllDLC = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/AllDLCPage'));
const UpcomingGames = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/UpcomingGamesPage'));
const NewReleases = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/NewReleasesPage'));
const Released = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/ReleasedPage'));
const EndingSoonContainer = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/EndingSoonPage'));
const LatestDealsContainer = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/LatestDealsPage'));
const FreeToPlayContainer = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/FreeToPlayPage'));
const MostWanted = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/MostWantedPage'));
const PlatformLinux = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/PlatformLinuxPage'));
const PlatformMac = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/PlatformMacPage'));
const GamesUnder = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/GamesUnderPage'));
const SteamDeck = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/SteamDeckPage'));
const TopSellers = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/TopSellersPage'));
const TrendingDealsContainer = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/TrendingDealsPage'));
const NotOnSale = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/NotOnSalePage'));
const GiftCards = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/GiftCardsPage'));
const MysteryPage = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/MysteryPage'));
const DeepDiscounts = lazy(() => import(/* webpackChunkName: "StaticCategory" */ 'Features/StaticCategory/pages/DeepDiscountsPage'));


const Vouchers = lazy(() => import(/* webpackChunkName: "Vouchers" */ 'Features/Vouchers/pages/Vouchers'));

const WishlistPage = lazy(() => import(/* webpackChunkName: "Wishlist" */ 'Features/Wishlist/pages/WishlistPage'));
const NotFound = lazy(() => import(/* webpackChunkName: "NotFound" */ 'Components/Feedback/NotFound/NotFound'));

const injectBoundary = WrappedComponent => props => (
  <ErrorBoundary>
    <WrappedComponent {...props} />
  </ErrorBoundary>
);

const noPrerender = !!window.location.hash;

const AppIntl = ({ siteTheme, siteBrand, disableMinHeight }) => (
  <Router history={history} basename="/">
    <div className={`${siteTheme === 'light' ? 'site-light-theme' : 'site-dark-theme'} brand-${siteBrand}`}>
      <>
        {noPrerender &&
          <MetaTag name="prerender-status-code" content="404" />
        }
        {noPrerender &&
          <MetaTag name="robots" content="noindex" />
        }
      </>
      {!isBot() &&
        <CookieConsentWrapper />
      }
      <ScrollToTop />
      <AnonymousId />

      <NavBar />

      <div className={classNames({ content: !disableMinHeight })}>
        <Suspense fallback={<SkeletonPlaceholder />}>
          <Switch>
            <Route exact path="/" component={HomePage} />

            <Route exact path="/black-friday" component={OnSalePage} />
            <Route exact path="/cyber-monday" component={CyberMonday} />

            <Route exact noPrerender path="/search" component={SearchPage} />
            <Route exact path="/blog" component={BlogPostHomePage} />
            <Route exact path="/blog/authors/:authorSlug" component={BlogPostHomePage} />
            <Route exact noPrerender path="/blog/preview" component={BlogPreviewPage} />
            <Route exact path="/blog/category/:category(games|news|develop)" component={BlogPostHomePage} />
            <Route exact path="/blog/:slug" component={BlogPostPage} />

            <Route exact path="/mac-games" component={PlatformMac} />
            <Route exact path="/linux-games" component={PlatformLinux} />
            <Route exact path="/steam-deck-games" component={SteamDeck} />
            <Route exact path="/deep-discounts" component={DeepDiscounts} />
            <Route exact path={'/games-under/:price(5|10|20)'} component={GamesUnder} />

            <Route exact path={'/:facet(vr-games)/:facets*'} component={FacetedFacetSearch} />

            <Route exact path={'/:facet(publishers|franchises|collections|categories|games-like)/'} component={FacetOrderedList} />
            <Route exact path={'/:facet(publishers|franchises|collections|categories)/:slug'} component={FacetSearch} />
            <Route exact path={'/games-like/:slug'} component={GamesLike} />

            <Route exact path="/all-games" component={AllGames} />
            <Route exact path="/all-dlc" component={AllDLC} />
            <Route exact path="/bundle" component={BundleCategoryPage} />
            <Route exact path="/bundle/books" component={BundleCategoryPage} />
            <Route exact path="/bundle/games" component={BundleCategoryPage} />
            <Route exact path="/bundle/software" component={BundleCategoryPage} />

            <Route exact path="/top-sellers" component={TopSellers} />
            <Route exact path="/on-sale" component={OnSalePage} />
            <Route exact path="/upcoming-games" component={UpcomingGames} />
            <Route exact path="/recently-added" component={Released} />
            <Route exact path="/new-releases" component={NewReleases} />
            <Route exact path="/latest-deals" component={LatestDealsContainer} />
            <Route exact path="/ending-soon" component={EndingSoonContainer} />
            <Route exact path="/most-wanted" component={MostWanted} />
            <Route exact path="/recently-viewed" component={RecentlyViewedPage} />
            <Route exact path="/trending-deals" component={TrendingDealsContainer} />
            <Route exact path="/full-price-games" component={NotOnSale} />
            <Route exact path="/free-to-play" component={FreeToPlayContainer} />
            <Route exact path="/fantasyverse" component={FantasyVersePage} />
            <Route exact path="/mystery" component={MysteryPage} />

            <Route exact path="/gift-cards/:filter(ea-play|nintendo|playstation|roblox|xbox|razer)?" component={GiftCards} />

            <Route exact path="/bundle/:slug" component={BundlePage} />
            <Route exact noPrerender componentProps={{ isPreview: true }} path="/bundle/preview/:id" component={BundlePage} />

            <Route exact path="/:type(game|dlc|book|comic|audio|video|software|gift-card)/:slug" component={ProductPage} />
            <Route exact noPrerender componentProps={{ isPreview: true }} path="/:type(game|dlc|book|comic|audio|video|software|gift-card)/preview/:id" component={ProductPage} />


            <Route exact noPrerender path="/pick-and-mix/preview/:id" component={PickAndMixContainer} />
            <Route exact noPrerender path="/image/preview/:id" component={ImagePreviewContainer} />
            <Route exact noPrerender path="/preview/featured-carousel" component={FeaturedCarouselFullPreviewContainer} />

            <Route exact path="/(game|dlc|software)/:slug/dlc" component={ProductDLCPage} />

            <Route exact path="/pick-and-mix/:slug" component={PickAndMixContainer} />

            <Route exact path="/about-us" component={AboutUsPage} />
            <Route exact path="/affiliates" component={AffiliatesPage} />
            <Route exact path="/community" component={CommunityPage} />
            <Route exact path="/referrals" component={ReferralsPage} />
            <Route exact path="/coupons-vouchers-codes" component={Vouchers} />
            <Route exact path="/cookie-policy" component={CookiePolicy} />

            <Route exact path="/browser-extension" component={ExtensionPage} />
            <Route exact noPrerender path="/browser-extension/uninstall" component={ExtensionUninstallPage} />

            <Route exact path="/cart" component={CheckoutPage} />

            <Route exact path="/redeem-code" component={RedeemCodePage} />
            <Route exact noPrerender path="/redeem-gift" component={RedeemGiftContainer} />
            <Route exact path="/wishlist" component={WishlistPage} />
            <Route exact noPrerender path="/wishlist/sync">
              <Redirect to="/wishlist?redirectFrom=/wishlist/sync" />
            </Route>
            <Route exact noPrerender path="/wishlist/:wishlistId" component={WishlistPage} />
            <Route exact noPrerender path="/new-password" component={ResetPassword} />

            <Route exact noPrerender path="/fandom-auth-return" component={FandomOauthReturnContainer} />

            <Route exact noPrerender path="/billing" component={BillingPage} />
            <Route exact noPrerender path="/payment" render={injectBoundary(PaymentPage)} />
            <Route exact noPrerender path="/payment-return/stripe/:orderId" component={PaymentReturn} />
            <Route exact noPrerender path="/payment-return/:orderId/data" component={PaymentReturn} />

            <Route exact path="/gift-guide" component={GiftGuide} />
            <Route exact path="/recommended" component={FanFavouritesContainer} />
            <Route exact path="/best-games-of-:year(2022|2023|2024|2025)" component={BestGamesOfPage} />

            <Route exact noPrerender path="/welcome" component={WelcomePage} />
            <Route exact noPrerender path="/welcome-new-user" component={WelcomeNewUserPage} />
            <Route exact noPrerender path="/partners/hp-omen" component={HpOmen} />
            <Route exact noPrerender path="/partners/hp-omen-gaming-hub" component={HpOmenGamingHub} />
            <Route exact noPrerender path="/pickaxe" component={Pickaxe} />

            <Route
              exact
              noPrerender
              path="/newconfirm/:token1/:token2/:date"
              component={AccountConfirm}
            />

            <Route
              exact
              noPrerender
              path="/email-change-confirm/:token"
              component={EmailChangeConfirm}
            />

            <Route exact noPrerender path="/password/reset/:token" component={PasswordReset} />
            <Route exact noPrerender path="/login/magic/:token" component={MagicLoginContainer} />

            <Route exact noPrerender path="/epic-return" component={EpicReturnContainer} />

            <Route exact noPrerender path="/receipt" component={ReceiptContainer} />

            <AccountRoute exact path="/account/login" component={LoginAndSecurity} />
            <AccountRoute exact path="/account/linked-accounts" component={LinkedAccounts} />
            <AccountRoute exact path="/account/rewards" component={CouponsAndRewards} />
            <AccountRoute exact path="/account/wishlist" component={WishlistPage} />
            <AccountRoute exact path="/account/wishlist/:wishlistId" component={WishlistPage} />
            <AccountRoute exact path="/account/preferencecenter" component={PreferenceCenter} />
            <AccountRoute exact path="/account/settings" component={WebsiteSettings} />
            <AccountRoute exact path="/account/payment" component={PaymentInformation} />
            <AccountRoute exact path="/account/contact" component={ContactPreferences} />
            <AccountRoute exact path="/account" component={AccountOverview} />
            <AccountRoute exact path="/support" component={Support} />
            <AccountRoute exact path="/orders" component={OrdersAndKeys} />
            <AccountRoute exact path="/orders/:id" component={OrderContainer} />
            <AccountRoute exact path="/orders/:id/:token1/:token2/:date" component={OrderContainer} />
            <AccountRoute exact path="/product-library" component={KeyLibrary} />
            <AccountRoute exact path="/reviews" component={OrderItemReviewsContainer} />
            <AccountRoute exact path="/review/:productId" component={OrderItemReviewForm} />

            <Route exact noPrerender path="/optin/:token" component={Optin} />

            <Route
              exact noPrerender path="/throw-error/:message?"
              component={injectBoundary((errorProps) => {
                throw new Error(errorProps.match.params.message);
              })}
            />

            <Route exact noPrerender path="/admin-login-as/:token" component={AdminLoginAs} />

            <Route exact noPrerender path="/splittest" component={SplitTestSwitch} />

            <Route exact noPrerender path="/competitions" component={Competitions} />

            <Route exact path="/showcase" component={Showcase} />
            <Route exact noPrerender path="/showcase/:slug" component={ShowcaseProduct} />

            <Route exact noPrerender path="/admin/competition/preview/:id" component={CompetitionPreviewContainer} />

            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </div>

      <VisibleVoucherContainer />

      <FooterGroupContainer />

      <ModalGroupContainer />

      <FeedbackPopupContainer />
    </div>
  </Router>
);

AppIntl.displayName = 'AppIntl';

AppIntl.propTypes = {
  siteTheme: PropTypes.string.isRequired,
  siteBrand: PropTypes.string,
  disableMinHeight: PropTypes.bool,
};

AppIntl.defaultProps = {
  siteBrand: 'fanatical',
  disableMinHeight: false,
};

const disableMinHeightPaths = ['/welcome-new-user', '/showcase'];

const mapStateToProps = ({
  siteTheme,
  siteBrand,
  router: { location: { pathname } },
  // Be very careful adding connect props to App, it can cause whole App
  // to rerender and may mess with the router - See DEV-4187
}) => ({
  siteTheme,
  siteBrand,
  disableMinHeight: disableMinHeightPaths.includes(pathname),
});

const ConnectedApp = connect(mapStateToProps)(AppIntl);


function App() {
  return (
    <IntlProvider locale={locale} messages={translations}>
      <ViewportProvider>
        <ErrorBoundary>
          <ConnectedApp />
        </ErrorBoundary>
      </ViewportProvider>
    </IntlProvider>
  );
}

export default App;
